import React from 'react';
import PeopleListCard from './PeopleListCard';
import MessageDraftCard from './MessageDraftCard';
import CirclesListCard from './CirclesListCard';
import TipsCard from './TipsCard';
import ConversationsListCard from './ConversationsListCard';
import UserProfileCard from './UserProfileCard';
import CircleDetailCard from './CircleDetailCard';
import EventsListCard from './EventsListCard';
import ContactInfoCard from './ContactInfoCard';
import UserStatsCard from './UserStatsCard';
import BadgesCard from './BadgesCard';
import ActionConfirmCard from './ActionConfirmCard';
import PostsListCard from './PostsListCard';
import VoiceLauncherCard from './VoiceLauncherCard';
import PersonChooserCard from './PersonChooserCard';
import WritebackReviewCard from './WritebackReviewCard';
import RoomInviteComposeCard from './RoomInviteComposeCard';
import RoomsListCard from './RoomsListCard';
import { InlineShell, type F8CardData, type OnAction } from './cardHelpers';
import '../../../styles/cards.css';

const CARD_COMPONENTS: Record<string, React.ComponentType<{ card: F8CardData; onAction?: OnAction }>> = {
  people_list: PeopleListCard,
  message_draft: MessageDraftCard,
  circles_list: CirclesListCard,
  tips: TipsCard,
  conversations_list: ConversationsListCard,
  user_profile: UserProfileCard,
  circle_detail: CircleDetailCard,
  events_list: EventsListCard,
  contact_info: ContactInfoCard,
  user_stats: UserStatsCard,
  badges: BadgesCard,
  action_confirm: ActionConfirmCard,
  posts_list: PostsListCard,
  voice_launcher: VoiceLauncherCard,
  // PPL-335 — the write-back cards. The server only emits them to clients that
  // can act on them (clientSupports 'writeback_cards'); everything older gets
  // a plain sentence instead of a card whose button would be inert.
  person_chooser: PersonChooserCard,
  writeback_review: WritebackReviewCard,
  // PPL-26 — inline f8 skill card the inviter uses to compose + send a
  // room invitation without leaving the chat. NOT a standalone page.
  // PPL-48 — register both old and new card type so cards emitted by old or
  // new f8-skills both render during the 1-release transition period.
  mediation_invite_compose: RoomInviteComposeCard,
  room_invite_compose: RoomInviteComposeCard,
  // PPL-450 — the rooms the user ALREADY has (get_my_rooms). Rows deep-link
  // into RoomDetail; propose_session still owns drafting a NEW invite.
  rooms_list: RoomsListCard,
};

export default function F8Card({ card, onAction }: { card: F8CardData; onAction?: OnAction }) {
  if (!card || !card.type) return null;

  const Component = CARD_COMPONENTS[card.type];
  if (!Component) {
    console.warn(`[F8Card] unknown card type: ${card.type}`);
    // PPL-337 — a card type this build does not know is a NEWER card type, not
    // a broken one, so it must read as part of the reply rather than as a
    // defect. The server ships the sentence it would have sent instead of the
    // card in `data.fallbackText`; fall back to a generic pointer only when it
    // is missing, and render nothing at all rather than an "unsupported" chip.
    const fallbackText =
      typeof card.data?.fallbackText === 'string' ? card.data.fallbackText.trim() : '';
    return (
      <InlineShell>
        <p className="f8c-fallback-text">
          {fallbackText || 'update pplkpr to see this.'}
        </p>
      </InlineShell>
    );
  }

  return <Component card={card} onAction={onAction} />;
}
